import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/data/client";
import { cn } from "@/lib/utils";
import type { ActivityKind, ActivityLog, ActivityStatus } from "@/data/types";
import { ThemeToggle } from "@/components/ThemeToggle";

type Period = { periodStart: string; periodEnd: string };

const STATUS_CONFIG: Record<
  ActivityStatus,
  { title: string; heading: string; subtitle: string; accent: string; chip: string }
> = {
  scheduled: {
    title: "Atividades Programadas",
    heading: "Atividades programadas",
    subtitle: "Tarefas ainda no prazo de execução — ordenadas por horário marcado",
    accent: "hsl(var(--chart-1))",
    chip: "bg-chart-1/15 text-chart-1",
  },
  completed: {
    title: "Atividades Finalizadas",
    heading: "Atividades finalizadas",
    subtitle: "Tarefas executadas e marcadas como concluídas no período",
    accent: "hsl(var(--chart-4))",
    chip: "bg-chart-4/15 text-chart-4",
  },
  missed: {
    title: "Atividades Não Realizadas",
    heading: "Atividades não realizadas",
    subtitle: "Tarefas que passaram do prazo sem execução — ação urgente da equipe",
    accent: "hsl(var(--semantic-danger))",
    chip: "bg-danger/15 text-danger",
  },
  "on-time": {
    title: "Atividades No Prazo",
    heading: "Atividades concluídas no prazo",
    subtitle: "Tarefas entregues dentro do SLA combinado com o cliente",
    accent: "hsl(var(--chart-3))",
    chip: "bg-chart-3/15 text-chart-3",
  },
};

const KIND_LABEL: Record<ActivityKind, string> = {
  call: "Ligação",
  meeting: "Reunião",
  "follow-up": "Follow-up",
  proposal: "Proposta",
  visit: "Visita",
};

const KIND_ICON: Record<ActivityKind, string> = {
  call: "📞",
  meeting: "👥",
  "follow-up": "✉️",
  proposal: "📄",
  visit: "📍",
};

const PAGE_SIZE = 10;

export function AtividadesPorStatus({
  status,
  period,
  onBack,
}: {
  status: ActivityStatus;
  period: Period;
  onBack: () => void;
}) {
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | ActivityKind>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const cfg = STATUS_CONFIG[status];

  const q = useQuery({
    queryKey: ["activities-by-status", status, period],
    queryFn: () => client.getActivitiesByStatus({ status, ...period, limit: 500 }),
  });

  const all = q.data?.data ?? [];
  const total = q.data?.total ?? 0;

  const assigneeOptions = useMemo(() => {
    const set = new Set<string>();
    all.forEach((a) => set.add(a.assignedUserName));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [all]);

  const visible = useMemo(() => {
    return all.filter((a) => {
      if (kindFilter !== "all" && a.kind !== kindFilter) return false;
      if (assigneeFilter !== "all" && a.assignedUserName !== assigneeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!a.title.toLowerCase().includes(s) && !a.contactName.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [all, kindFilter, assigneeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page]
  );

  return (
    <div className="min-h-screen bg-bg-primary px-12 py-10 space-y-8">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cfg.accent }} />
          <span className="text-sm font-bold tracking-[0.2em] text-text-primary">HORIZON</span>
          <span className="text-sm text-text-muted">·</span>
          <span className="text-sm font-medium text-text-secondary">Clínica de Radiologia Odontológica</span>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 rounded-full border border-border-subtle bg-bg-card px-3.5 py-2 text-sm font-medium text-text-secondary transition hover:border-border-strong hover:text-text-primary"
          >
            <span>←</span>
            <span>Voltar ao Dashboard</span>
          </button>
        </div>
      </div>

      {/* Breadcrumb + title */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <button onClick={onBack} className="transition hover:text-text-secondary" type="button">
            Dashboard Executivo
          </button>
          <span>/</span>
          <span>Atividades</span>
          <span>/</span>
          <span className="text-text-secondary">{cfg.heading}</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-text-primary">{cfg.heading}</h1>
        <p className="text-[15px] text-text-secondary">{cfg.subtitle}</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-5">
        <SummaryCard label="Total no período" value={total.toString()} />
        <SummaryCard label="Após filtros" value={visible.length.toString()} caption="itens exibidos abaixo" />
        <SummaryCard
          label="Tipos distintos"
          value={new Set(visible.map((a) => a.kind)).size.toString()}
          caption="diversidade de atividades"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border-subtle bg-bg-card p-4">
        <input
          type="search"
          placeholder="Buscar por título ou contato…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="h-9 flex-1 min-w-[220px] rounded-md border border-border-subtle bg-bg-primary px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand-amber focus:outline-none"
        />
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Tipo:</span>
          <select
            value={kindFilter}
            onChange={(e) => {
              setKindFilter(e.target.value as "all" | ActivityKind);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            <option value="all">Todos</option>
            {(Object.keys(KIND_LABEL) as ActivityKind[]).map((k) => (
              <option key={k} value={k}>{KIND_LABEL[k]}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <span>Responsável:</span>
          <select
            value={assigneeFilter}
            onChange={(e) => {
              setAssigneeFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-border-subtle bg-bg-primary px-2 text-sm text-text-primary focus:border-brand-amber focus:outline-none"
          >
            {assigneeOptions.map((a) => (
              <option key={a} value={a}>{a === "all" ? "Todos" : a}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-xs text-text-muted">
          {visible.length} de {total}
        </div>
      </div>

      {/* Table */}
      <section className="rounded-lg border border-border-subtle bg-bg-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-bg-secondary">
            <tr className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">
              <th className="px-5 py-3 text-left">Atividade</th>
              <th className="px-5 py-3 text-left">Contato</th>
              <th className="px-5 py-3 text-left">Responsável</th>
              <th className="px-5 py-3 text-left">Quando</th>
              <th className="px-5 py-3 text-right">Duração</th>
              <th className="px-5 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-muted">
                  Carregando…
                </td>
              </tr>
            )}
            {!q.isLoading && visible.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-sm text-text-muted">
                  Nenhuma atividade encontrada com os filtros atuais.
                </td>
              </tr>
            )}
            {pageItems.map((a, i) => (
              <Row key={a.activityId} activity={a} chip={cfg.chip} isLast={i === pageItems.length - 1} />
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border-subtle px-6 py-3">
            <span className="text-xs text-text-muted">
              Exibindo <span className="font-semibold text-text-primary">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, visible.length)}</span> de {visible.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
                  page === 1 ? "cursor-not-allowed text-text-muted/40" : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
                )}
              >
                ‹
              </button>
              <span className="px-2 text-xs text-text-secondary">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-md text-sm transition",
                  page === totalPages ? "cursor-not-allowed text-text-muted/40" : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
                )}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ activity, chip, isLast }: { activity: ActivityLog; chip: string; isLast: boolean }) {
  const when = new Date(activity.scheduledAt).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <tr
      className={cn(
        "text-sm transition hover:bg-bg-card-hover",
        !isLast && "border-b border-border-subtle"
      )}
    >
      <td className="px-5 py-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 text-lg leading-none" aria-hidden>{KIND_ICON[activity.kind]}</span>
          <div>
            <div className="font-medium text-text-primary">{activity.title}</div>
            <div className="text-xs text-text-muted">{KIND_LABEL[activity.kind]}</div>
          </div>
        </div>
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-text-on-accent"
            style={{ backgroundColor: activity.avatarColor }}
          >
            {getInitials(activity.contactName)}
          </span>
          <span className="text-text-secondary">{activity.contactName}</span>
        </div>
      </td>
      <td className="px-5 py-3 text-text-secondary">{activity.assignedUserName}</td>
      <td className="px-5 py-3 text-text-secondary">{when}</td>
      <td className="px-5 py-3 text-right text-text-secondary">
        {activity.durationMinutes ? `${activity.durationMinutes} min` : "—"}
      </td>
      <td className="px-5 py-3">
        <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold", chip)}>
          {STATUS_CONFIG[activity.status].heading.split(" ")[1] || STATUS_CONFIG[activity.status].heading}
        </span>
      </td>
    </tr>
  );
}

function SummaryCard({ label, value, caption }: { label: string; value: string; caption?: string }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-bg-card p-6 space-y-3">
      <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-text-muted">{label}</span>
      <div className="text-3xl font-bold tracking-tight text-text-primary">{value}</div>
      {caption && <p className="text-[13px] text-text-muted">{caption}</p>}
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
